import React, { FC, useEffect, useState } from 'react';

import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { RequestsService } from 'services/requests/request';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import RecruiterIncomingRequests from 'components/shared/RecruiterIncomingRequests';
import Popup from 'components/shared/Popups/Popup/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';
import {
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Pagination
} from '@material-ui/core';
import Image from 'components/image/Image';
import OutgoingRequests from 'components/shared/OutgoingRequests/Index';
import Navbar from 'components/Navbar';
import MatchResultHeader from 'components/matchResultHeader';
import { useParams } from 'react-router';
import Footer from 'components/Footer/Index';
import NoData from 'components/shared/NoData/Index';
import { getDateFormatter } from 'helpers/dateConverter';
import { STATUS_TYPES } from 'core/constants/jobCardConstant';
import { useDebounce } from 'helpers/useDebounce';

import './Index.scss';

const FindCandidate: FC = (): JSX.Element => {
  const { t } = useLocales();
  const { applications } = useSelector(
    (state: RootState) => state.applications
  );
  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const { singleRequest }: any = useSelector(
    (state: RootState) => state.requests
  );
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const { id } = useParams();
  const { singleJob: job } = useSelector((state: RootState) => state.jobs);
  const [filteredApplications, setFilteredApplications] = useState<any>([]);
  const [lastFilterType, setLastFilterType] = useState<number | null>();
  const [filteredJobs, setFilteredJobs] = useState<any>([]);
  const [lastFilterStatus, setLastFilterStatus] = useState<number | null>();

  const [statusCounts, setStatusCounts] = useState<any>({
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0
  });

  const [jobPosts, setJobPosts] = useState([]);
  const [selectedJob, setSelectedJob] = useState<any>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [outgoingSearchQuery, setOutgoingSearchQuery] = useState<string>('');

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedOutgoingSearchQuery = useDebounce(outgoingSearchQuery, 500);

  const getJobById = () => {
    JobService.getJobByIdOrSlug(id ? +id : 0);
  };

  useEffect(() => {
    getJobById();
  }, []);

  const [applicationCounts, setApplicationCounts] = useState<{
    incomingCount: number;
    hiredCount: number;
  }>({
    incomingCount: 0,
    hiredCount: 0
  });

  const handleModal = (open: boolean, id: number) => {
    setOpenModal(open);

    let element;

    // Check in applications first
    element = applications?.results?.find((el: any) => el.id === id);

    // If not found in applications, check in singleRequest
    if (!element) {
      element = singleRequest?.results?.find((el: any) => el.id === id);
    }

    if (!element) {
      return;
    }

    // Set the selected request
    setSelectedRequest(element);
  };

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  useEffect(() => {
    if (selectedJob && jobs.results) {
      const filteredApps = applications?.results.filter(
        (app: any) => app.job.title === selectedJob
      );
      setFilteredApplications(filteredApps);
    } else {
      setFilteredApplications(applications?.results || []);
    }
  }, [selectedJob, applications]);

  useEffect(() => {
    const newStatusCounts: any = { ...statusCounts };
    Object.keys(newStatusCounts).forEach((key) => {
      newStatusCounts[key] = 0;
    });
    singleRequest?.results?.forEach((result: { status: number }) => {
      const status = result?.status;
      if (status in newStatusCounts) {
        newStatusCounts[status]++;
      }
    });
    setStatusCounts(newStatusCounts);
    setFilteredJobs(singleRequest?.results);
  }, [singleRequest]);

  useEffect(() => {
    const newApplications = applications?.results?.reduce(
      (count: any, el: { stage: number }) => {
        if (el.stage === 0) {
          count++;
        }
        return count;
      },
      0
    );

    const hired = applications?.results?.reduce(
      (count: any, el: { stage: number }) => {
        if (el.stage === 5) {
          count++;
        }
        return count;
      },
      0
    );

    setApplicationCounts({
      hiredCount: hired,
      incomingCount: newApplications
    });
  }, [filteredApplications]);

  useEffect(() => {
    const jobNames = jobs?.results?.map((el: any) => el.title);
    setJobPosts(jobNames);
  }, [jobs]);

  const changeStage = (id: number, data: any) => {
    JobService.manageJob(id, data);
  };

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [outgoingLimit, setOutgoingLimit] = useState(10);
  const [outgoingOffset, setOutgoingOffset] = useState(0);
  const [outgoingTotalCount, setOutgoingTotalCount] = useState(0);

  // Update offset when page or limit changes (incoming)
  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  // Update offset when page or limit changes (outgoing)
  useEffect(() => {
    setOutgoingOffset((outgoingPage - 1) * outgoingLimit);
  }, [outgoingPage, outgoingLimit]);

  // Fetch applications with backend pagination
  const getApplications = (query?: string): void => {
    JobService.getApplications(id ? +id : 0, query, limit, offset);
  };

  // Fetch outgoing requests with backend pagination
  const getCandidateRequests = (query?: string): void => {
    RequestsService.getRequest(
      id ? +id : 0,
      query,
      outgoingLimit,
      outgoingOffset
    );
  };

  // Refetch data when pagination changes
  useEffect(() => {
    getApplications();
  }, [limit, offset]);
  useEffect(() => {
    getCandidateRequests();
  }, [outgoingLimit, outgoingOffset]);

  // Handle search queries
  useEffect(() => {
    getApplications(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    getCandidateRequests(debouncedOutgoingSearchQuery);
  }, [debouncedOutgoingSearchQuery]);

  // Set total counts from API response
  useEffect(() => {
    if (applications && applications.count !== undefined) {
      setTotalCount(applications.count);
    }
  }, [applications]);
  useEffect(() => {
    if (singleRequest && singleRequest.count !== undefined) {
      setOutgoingTotalCount(singleRequest.count);
    }
  }, [singleRequest]);

  const cancelHandler = (id: number) => {
    JobService.rejectApplication(id);
  };

  const jobNames = () => {
    JobService.getJobs();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handleOutgoingSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setOutgoingSearchQuery(query);
  };

  const stageOutgoingHandler = (id: number, data: any) => {
    RequestsService.changeRequest(data, id, 'find-candidate');
  };

  useEffect(() => {
    jobNames();
  }, []);

  const handleStagefilter = (type: number) => {
    let filteredApps = null;
    if (lastFilterType === type) {
      filteredApps = applications?.results;
      setFilteredApplications(filteredApps);
      setLastFilterType(null);
    } else {
      filteredApps = applications?.results.filter((app) => app.stage === type);
      setFilteredApplications(filteredApps);
      setLastFilterType(type);
    }
  };

  const handleStatusFilter = (type: number) => {
    let filteredJob = null;
    if (lastFilterStatus === type) {
      filteredJob = singleRequest?.results;
      setFilteredJobs(filteredJob);
      setLastFilterStatus(null);
    } else {
      filteredJob = singleRequest?.results?.filter(
        (app: { status: number }) => app.status === type
      );
      setFilteredJobs(filteredJob);
      setLastFilterStatus(type);
    }
  };

  const handleSetOpen = (id: number, type: string) => {
    handleModal(true, id);
  };

  const handleAccept = async (id: number) => {
    await JobService.acceptApplication(id);
    getApplications();
  };

  const handleDecline = async (id: number) => {
    await JobService.denyApplication(id);
    getApplications();
  };

  // Reset page to 1 when applications change (e.g., after a filter/search in the future)
  useEffect(() => {
    setPage(1);
  }, [applications]);
  // Reset outgoingPage to 1 when singleRequest changes
  useEffect(() => {
    setOutgoingPage(1);
  }, [singleRequest]);

  return (
    <>
      <Navbar />
      <MatchResultHeader data={job} />
      <div className="candidateOver">
        <div className="candidateOver__right">
          <div className="candidateOver__right__top">
            <span className="candidateOver__right__top-title">
              {t('Incoming request')}
            </span>
            <div className="candidateOver__right__top__counts">
              <button
                className="candidateOver__right__top__counts-warn btn"
                onClick={() => handleStagefilter(0)}
              >
                {lastFilterType === 0 && <Image name="check.svg" />}
                {t('New applicant ')}
                {`(${applicationCounts.incomingCount})`}
              </button>
              <button
                className="candidateOver__right__top__counts-pass btn"
                onClick={() => handleStagefilter(5)}
              >
                {lastFilterType === 5 && <Image name="check.svg" />}
                {t('Candidate Hired ')}
                {`(${applicationCounts.hiredCount})`}
              </button>
            </div>
            <div className="search-container">
              <TextField
                value={searchQuery}
                onChange={handleSearch}
                className="dropdown-search"
                placeholder="Search"
                variant="outlined"
              />
              <Select
                displayEmpty
                value={selectedJob}
                className="select_jobs"
                placeholder={t('Select your job post')}
                onChange={(e) => setSelectedJob(e?.target?.value)}
                renderValue={(selected) => {
                  if (selected?.length === 0) {
                    return <em>{t('Select your job post')}</em>;
                  }
                  return selected;
                }}
                IconComponent={() => (
                  <>
                    <Image name="right-arrow.svg" />
                  </>
                )}
                inputProps={{ 'aria-label': 'Without label' }}
              >
                {jobPosts?.map((el: any) => (
                  <MenuItem value={el} key={el}>
                    {el}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          <div className="candidateOver__right__bottom">
            {applications === null ? (
              <div className="loadingContainer">
                <CircularProgress className="loading" />
              </div>
            ) : filteredApplications.length ? (
              filteredApplications?.map((item: any) => (
                <RecruiterIncomingRequests
                  key={item.id}
                  id={item.id}
                  name={item.candidate.name}
                  email={item.candidate.email}
                  sub_title={item.candidate.pitch}
                  icon={item.candidate.avatar}
                  score={item.score}
                  culture_score={item.culture_score}
                  date={item.created_at}
                  industry={item.candidate.industries}
                  stage={item.stage}
                  status={item.status}
                  hired_at={item.offered_at}
                  handleCancel={cancelHandler}
                  handleOpenModal={handleSetOpen}
                  handleAccept={handleAccept}
                  handleDecline={handleDecline}
                  jobTitle={item?.job?.title || ''}
                />
              ))
            ) : (
              <div className="no_data">
                <NoData />
              </div>
            )}
            {/* Pagination for incoming requests */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Pagination
                className="pagination"
                count={Math.ceil((applications?.count || 0) / limit)}
                page={page}
                onChange={(_, value) => setPage(value)}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </div>
          </div>
        </div>

        <div className="candidateOver__right">
          <div className="candidateOver__right__top">
            <span className="candidateOver__right__top-title">
              {t('Outgoing requests')}
            </span>
            <div className="candidateOver__right__top__counts">
              {STATUS_TYPES?.map((statusType: any) => (
                <button
                  onClick={() => handleStatusFilter(statusType.value)}
                  key={statusType.value}
                  className="candidateOver__right__top__counts-pass btn"
                >
                  {lastFilterStatus === statusType.value && (
                    <Image name="check.svg" />
                  )}
                  {t(statusType.label)} ({statusCounts[statusType.value]})
                </button>
              ))}
            </div>
            <div className="search-container">
              <TextField
                value={outgoingSearchQuery}
                onChange={handleOutgoingSearch}
                className="dropdown-search"
                placeholder="Search"
                variant="outlined"
              />
            </div>
          </div>
          <div className="candidateOver__right__bottom">
            {singleRequest === null ? (
              <div className="loadingContainer">
                <CircularProgress className="loading" />
              </div>
            ) : filteredJobs?.length ? (
              filteredJobs?.map((item: any, index: any) => (
                <OutgoingRequests
                  key={item.id}
                  id={item.id}
                  name={item.candidate.name}
                  email={item.candidate.email}
                  sub_title={item.candidate.pitch}
                  icon={item.candidate.avatar}
                  score={item.score}
                  culture_score={item.culture_score}
                  date={item.created_at}
                  modified_at={item.modified_at}
                  industry={item.candidate.industries}
                  stage={item.stage}
                  responded_at={item.responded_at}
                  status={item.status}
                  stageOutgoingHandler={stageOutgoingHandler}
                  setOpen={() => handleModal(true, item?.id)}
                  jobTitle={
                    item?.origin_job?.title || item?.origin_profile?.title || ''
                  }
                />
              ))
            ) : (
              <div className="no_data">
                <NoData />
              </div>
            )}
            {/* Pagination for outgoing requests */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Pagination
                className="pagination"
                count={Math.ceil((singleRequest?.count || 0) / outgoingLimit)}
                page={outgoingPage}
                onChange={(_, value) => setOutgoingPage(value)}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </div>
          </div>
        </div>
      </div>
      {openModal && selectedRequest && (
        <Popup
          className="popUp"
          title={t('Match Profile')}
          description={selectedRequest?.job?.title}
          onClose={handleClosePopup}
        >
          <ProfilePopUp
            selectedRequest={selectedRequest}
            getJob={
              selectedRequest?.job ||
              selectedRequest?.origin_job ||
              selectedRequest?.origin_profile
            }
            id={selectedRequest?.id}
            onClick={() =>
              changeStage(selectedRequest?.id, {
                status: 4
              })
            }
            status={selectedRequest.status}
            isCandidate={false}
          />
        </Popup>
      )}
      <div className="footers">
        <Footer />
      </div>
    </>
  );
};

export default FindCandidate;
